import {
  addWordToDictionary,
  deleteWordFromDictionary,
  getDictionaryExamples,
  updateDictionary,
} from "../../../../../../services/dictionary";
import { ReactComponent as ExampleSentenceIcon } from "images/example_sentence_icon.svg";
import exampleSentenceImg from "images/example_sentence_icon.svg";
import quickReferenceAllImg from "../../../../../../images/quick_reference_all.svg";
import * as React from "react";
import {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@material-ui/core";
import translate from "../../../../../SelectionBehaviour/images/translate.svg";
import AppDialogWithHeader from "../../../../../AppDialog/AppDialogWithHeader";
import headerImg from "../../../../../../pages/admin/images/plus-circle.svg";
import TalaeraSpinner from "../../../../../TalaeraSpinner/TalaeraSpinner";
import define from "../../../../../SelectionBehaviour/images/define.svg";
import audioImg from "../../components/DictionaryItem/images/audio.svg";
import Action from "../../../../../formElements/Action/Action";
import Button from "../../../../../formElements/Button/Button";
import Input from "../../../../../formElements/Input/Input";
import trash from "../../../../../../images/trash.svg";
import { withStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { ExpandMore, MoreVert } from "@material-ui/icons";
import "./SaveWords.css";
import { logError } from "../../../../../../services/logging";
import { TalaeraError } from "../../../../../../model/general";
import TalaeraRoutes from "components/routes/TalaeraRoutes";
import { useNavigate } from "react-router-dom";
import Dialog from "components/AppDialog/components/Dialog/Dialog";
import { ReactComponent as CloseIcon } from "images/close.svg";
import { ReactComponent as LockIcon } from "images/lock.svg";
import { HStack, VStack } from "components/Stack";
import { getErrorComponents } from "services/util";
import { ReactComponent as ForbiddenImg } from "images/403.svg";
import { Trans, useTranslation } from "react-i18next";
import Paywall from "../../../../../Paywall";
import useUser from "../../../../../../hooks/useUser";

const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "#01BCD4",
      borderColor: "#01BCD4",
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: "#E0E0E0",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#E0E0E0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#01BCD4",
      },
    },
  },
})(TextField);

interface IAccordionViewProps {
  header: ReactNode;
  expanded: boolean;
  children: ReactNode;
  onChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

const AccordionView = (props: IAccordionViewProps) => {
  return (
    <Accordion
      className="accordion-container"
      expanded={props.expanded}
      onChange={props.onChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-content"
        id="panel-header"
        className="accordion-summary"
      >
        {props.header}
      </AccordionSummary>
      <AccordionDetails>
        <div className="accordion-details">{props.children}</div>
      </AccordionDetails>
    </Accordion>
  );
};

interface IWord {
  id?: string;
  pos: string;
  word: string;
  definition: string;
  translation: string;
  audio?: string;
  notes: string;
}

interface ISaveWords extends IWord {
  expanded: boolean;
  onErrorOccurred: (e: any) => void;
  onModeChanged: (value: string) => void;
  onRefresh: () => void;
  onNotesChange: (id: string, notes: string) => void;
  onDeleteWord: (word: IWord) => void;
  onTextChanged: (text: string) => void;
  onChangePanel: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
  onOpenExampleDialog: (word: { id: string; word: string }) => void;
  onShowSpinner: (status: boolean) => void;
}

export const SaveWords = (props: ISaveWords) => {
  const { t } = useTranslation();
  const [delayedNotes, setDelayedNotes] = useState<string | null>(null);
  const [wordData, setWordData] = useState<IWord>({
    id: props.id || "",
    pos: props.pos || "",
    word: props.word || "",
    definition: props.definition || "",
    translation: props.translation || "",
    audio: props.audio || "",
    notes: props.notes || "",
  });

  useEffect(() => {
    setWordData({
      id: props.id || "",
      pos: props.pos || "",
      word: props.word || "",
      definition: props.definition || "",
      translation: props.translation || "",
      audio: props.audio || "",
      notes: props.notes || "",
    });
  }, [props]);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setWordData({ ...wordData, [e.target.name]: e.target.value });
  };

  const handleDelete = (e: MouseEvent) => {
    props.onShowSpinner(true);
    e.stopPropagation();
    if (wordData.id) {
      deleteWordFromDictionary(
        wordData.id,
        () => {
          props.onShowSpinner(false);
          props.onDeleteWord(wordData);
        },
        (err: any) => {
          props.onShowSpinner(false);
          props.onErrorOccurred(err);
        }
      );
    }
  };

  const handleShowExampleSentences = () => {
    props.id && props.onOpenExampleDialog({ id: props.id, word: props.word });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => setDelayedNotes(wordData.notes), 500);
    return () => clearTimeout(timeoutId);
  }, [wordData.notes]);

  useEffect(() => {
    if (wordData.id && props.expanded && delayedNotes !== null) {
      props.onNotesChange(wordData.id, delayedNotes);
    }
    // eslint-disable-next-line
  }, [delayedNotes]);

  return (
    <div className="save-item">
      <AccordionView
        expanded={props.expanded}
        onChange={props.onChangePanel}
        header={
          <SaveWordHeader
            pos={props.pos}
            word={props.word}
            audio={props.audio}
            onDelete={handleDelete}
            onModeChanged={props.onModeChanged}
            onTextChanged={props.onTextChanged}
            onChangePanel={props.onChangePanel}
          />
        }
      >
        <MyNotes value={wordData.notes} label={t("Tool.myNote")} onChange={onChange} />
        <button className="explain-button" onClick={handleShowExampleSentences}>
          <img src={quickReferenceAllImg} alt="quick-reference-all" />
          <span>{t("Tool.showExample")}</span>
        </button>
      </AccordionView>
    </div>
  );
};

interface ISaveWordHeader {
  audio?: string;
  word: string;
  pos: string;
  onDelete: (e: MouseEvent) => void;
  onModeChanged?: (mode: string) => void;
  onTextChanged?: (text: string) => void;
  onChangePanel?: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

const SaveWordHeader = (props: ISaveWordHeader) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onModeChanged = (mode: string) => () => {
    if (props.onTextChanged) {
      props.onTextChanged(props.word);
    }

    setTimeout(() => {
      if (props.onModeChanged) {
        props.onModeChanged(mode);
      }
    }, 100);
  };

  const handleOpenActionMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setAnchorEl(null);
  };

  const handleModeChanged = (event: MouseEvent, mode: string) => {
    event.stopPropagation();
    onModeChanged(mode)();
    handleCloseActionMenu();
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    props.onDelete(event);
    handleCloseActionMenu();
  };

  return (
    <div className="save-word-header">
      <div className="save-word-header-container">
        <Pronunciation source={props.audio} />
        <div className="sub-header-container">
          <span>
            <span className="def title" title={props.word}>
              {props.word}
            </span>
          </span>
          <span className="speech" title={props.pos}>
            {props.pos}
          </span>
        </div>
      </div>

      <div className="save-word-header-actions">
        <Action className="btn action-with-image" onClick={onModeChanged("define")}>
          <img src={define} height={20} width={18} alt="save word" />
        </Action>
        <Action className="btn action-with-image" onClick={onModeChanged("translate")}>
          <img src={translate} height={20} width={18} alt="save word" />
        </Action>
        <Action className="btn action-with-image" onClick={props.onDelete}>
          <img src={trash} height={20} width={18} alt="content" />
        </Action>
      </div>

      <div className="save-word-header-mobile-actions">
        <IconButton className="save-word-header-expand">
          <ExpandMore />
        </IconButton>
        <IconButton className="menu-button" onClick={handleOpenActionMenu}>
          <MoreVert />
        </IconButton>
        <Menu
          className="save-words-mobile-action-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={(event: MouseEvent) => handleModeChanged(event, "define")}>
            <img src={define} height={20} width={18} alt="save word" />
            {t("Tool.Define")}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={(event: MouseEvent) => handleModeChanged(event, "translate")}
          >
            <img src={translate} height={20} width={18} alt="save word" />
            {t("Tool.Translate")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete}>
            <img src={trash} height={20} width={18} alt="delete" />
            {t("Tool.Delete")}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

interface IPronunciation {
  source?: string;
}

const Pronunciation = (props: IPronunciation) => {
  let audioElement: any;

  const onAudioPlay = (e: MouseEvent) => {
    e.stopPropagation();
    audioElement && audioElement.play && audioElement.play();
  };

  return (
    <div className={"audio-container"}>
      <Action className="audio-btn" onClick={onAudioPlay}>
        <img src={audioImg} height={18} width={17} alt="audio" />
      </Action>
      <audio controlsList="nodownload" ref={c => (audioElement = c)}>
        <source src={props.source} />
      </audio>
    </div>
  );
};

interface ICustomFormProps {
  value: string;
  label: string;
  isDefinition?: boolean;
  isTranslation?: boolean;
  onModeChanged?: (data: string) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (data: SyntheticEvent) => void;
}

const CustomDefinition = (props: ICustomFormProps) => {
  return (
    <div className="my-definition-text">
      <form onSubmit={props.onSubmit}>
        <CssTextField
          id="outlined-basic"
          name="definition"
          size="small"
          value={props.value}
          onChange={props.onChange}
          onBlur={props.onSubmit}
          placeholder={"Enter your definition here..."}
          label={props.label}
          variant="outlined"
          color={"primary"}
          fullWidth
        />
      </form>
      {props.isDefinition && (
        <span className="view-dict title">
          <img src={translate} height={20} width={18} alt="save word" />
          VIEW DICTIONARY DEFINITION
        </span>
      )}
    </div>
  );
};

const CustomTranslation = (props: ICustomFormProps) => {
  const onModeChanged = (mode: string) => () => {
    if (props.onModeChanged) {
      props.onModeChanged(mode);
    }
  };

  return (
    <div className={"my-translation-text"}>
      <form onSubmit={props.onSubmit}>
        <CssTextField
          id="outlined-basic"
          name="translation"
          fullWidth
          value={props.value}
          onChange={props.onChange}
          onBlur={props.onSubmit}
          placeholder={"Enter your definition here..."}
          label={props.label}
          variant="outlined"
          color={"primary"}
        />
      </form>
      {props.isTranslation && (
        <span className="title view-dict" onClick={onModeChanged("translate")}>
          <img src={translate} height={20} width={18} alt="save word" />
          VIEW DICTIONARY TRANSLATION
        </span>
      )}
    </div>
  );
};

interface IMyNotes {
  value: string;
  label: string;
  isDefinition?: boolean;
  isTranslation?: boolean;
  onModeChanged?: (data: string) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const MyNotes = (props: IMyNotes) => {
  const { t } = useTranslation();
  return (
    <div className={"my-notes-text"}>
      <CssTextField
        id="outlined-basic"
        name="notes"
        size="small"
        fullWidth
        value={props.value}
        onChange={props.onChange}
        placeholder={`${t("Tool.enterNoteHere")}...`}
        label={props.label}
        variant="outlined"
        color={"primary"}
      />
    </div>
  );
};

interface IAddWordHeaderProps {
  onSubmit: (event: SyntheticEvent) => void;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDelete: React.MouseEventHandler<HTMLAnchorElement>;
}

const AddWordHeader = (props: IAddWordHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className="save-word-header">
      <div className="save-word-header-containevr">
        <form onSubmit={props.onSubmit}>
          <Input
            name="word"
            className={"input"}
            value={props.value}
            onChange={props.onChange}
            placeholder={t("Tool.enterWordPhrase")}
          />
        </form>
      </div>
      <div>
        <span className="view-dict title">
          <img src={translate} height={20} width={18} alt="save word" />
        </span>
        <Action className="btn action-with-image" onClick={props.onDelete}>
          <img src={trash} height={20} width={18} alt="delete word" />
        </Action>
      </div>
    </div>
  );
};

interface IAddWord {
  onErrorOccurred: (e: any) => void;
  onClose: () => void;
}

export const AddWord = (props: IAddWord) => {
  const [wordData, setWordData] = useState<IWord>({
    id: "",
    pos: "",
    audio: "",
    word: "",
    definition: "",
    translation: "",
    notes: "",
  });

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWordData({ ...wordData, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    if (wordData.word) {
      if (wordData.id) {
        updateDictionary(
          wordData.id,
          {
            word: wordData.word,
            definition: wordData.definition,
            translation: wordData.translation,
            notes: wordData.notes,
          },
          (res: IWord) => {
            setWordData({ ...wordData, id: res.id });
          },
          (e: any) => {
            props.onErrorOccurred(e);
          }
        );
      } else {
        addWordToDictionary(
          {
            word: wordData.word,
            definition: wordData.definition,
            translation: wordData.translation,
            notes: wordData.notes,
          },
          (res: IWord) => {
            setWordData({ ...wordData, id: res.id });
          },
          (e: any) => {
            props.onErrorOccurred(e);
          }
        );
      }
    }
  };

  const handleDelete: React.MouseEventHandler<HTMLAnchorElement> = event => {
    event.stopPropagation();

    if (wordData.word && wordData.id) {
      deleteWordFromDictionary(
        wordData.id,
        () => {
          setWordData({
            id: "",
            pos: "",
            word: "",
            definition: "",
            translation: "",
            notes: "",
          });
          props.onClose();
        },
        (e: any) => {
          props.onErrorOccurred(e);
        }
      );
    }
  };

  /* FIXME: Temporarily added onChange & expanded property to fix compilation error. */
  return (
    <AccordionView
      header={
        <AddWordHeader
          value={wordData.word}
          onChange={onChange}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
        />
      }
      onChange={() => {}}
      expanded={false}
    >
      <CustomDefinition
        label="My Definition"
        value={wordData.definition}
        onChange={onChange}
        onSubmit={handleSubmit}
      />
      <CustomTranslation
        label="My Translation"
        value={wordData.translation}
        onChange={onChange}
        onSubmit={handleSubmit}
      />
    </AccordionView>
  );
};

type AddNewWordDialogProps = {
  open: boolean;
  onClose: (refresh?: boolean) => void;
  onListRefresh: () => void;
};

export const AddNewWordDialog = ({
  open,
  onClose,
  onListRefresh,
}: AddNewWordDialogProps) => {
  const { t } = useTranslation();
  const [word, setWord] = useState("");
  const [notes, setNotes] = useState("");

  const handleChangeWord = (e: ChangeEvent<HTMLInputElement>) => {
    setWord(e.target.value);
  };

  const handleChangeNote = (e: ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    addWordToDictionary(
      {
        word: word,
        notes: notes,
      },
      () => {
        onListRefresh();
        onClose(true);
      },
      (e: any) => {
        logError(e);
        // onErrorOccurred(e);
      }
    );
  };

  let formActions = (
    <div className="form-actions">
      <Action className="btn cancel" onClick={onClose}>
        {t("Common.Cancel")}
      </Action>
      <Button className="btn submit" onClick={handleSubmit}>
        {t("Tool.addNewWordBtn")}
      </Button>
    </div>
  );

  return (
    <AppDialogWithHeader
      className="add-word"
      headerImg={headerImg}
      showDialog={open}
      onClose={onClose}
      title={t("Tool.addNewWord")}
      formActions={formActions}
    >
      <div className="width-100">
        <label htmlFor="word">{t("Tool.addNewWord")}</label>
      </div>
      <div className="width-100">
        <Input type="text" value={word} onChange={handleChangeWord} required={true} />
      </div>

      <div className="width-100">
        <label htmlFor="note">{t("Tool.addNote")}</label>
      </div>
      <div className="width-100">
        <Input type="text" value={notes} onChange={handleChangeNote} required={false} />
      </div>
    </AppDialogWithHeader>
  );
};

type SentencesExampleDialogProps = {
  open: boolean;
  word: { word: string; id: string };
  onClose: () => void;
};

export const SentencesExampleDialog = ({
  open,
  word,
  onClose,
}: SentencesExampleDialogProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [examples, setExamples] = useState<string[]>([]);
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<TalaeraError | null>();
  const [showLimitError, setShowLimitError] = useState(false);
  const user = useUser(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchExamples = async () => {
    setIsLoading(true);
    setError(null);

    for (let attempts = 0; attempts < 5; attempts++) {
      let response;
      try {
        response = await getDictionaryExamples({ id: word.id, count: 3 });
      } catch (e: any) {
        if (e?.statusCode === 429) {
          setShowLimitError(true);
          setCount(0);
          setExamples([]);
          setError(null);
          break;
        }

        logError("Got an error during loading examples", e);
        setCount(0);
        setExamples([]);
        setShowLimitError(false);
        setError(e.errors as TalaeraError);
        break;
      }
      if (response.noContent) {
        // 204 No Content is a signal by server that it still generates examples.
        // Try again in 3 seconds.
        await new Promise(resolve => setTimeout(resolve, 3000));
      } else {
        // Successfully loaded data, show it.
        setExamples(response.body.list);
        break;
      }
    }
    setIsLoading(false);
  };

  const generateMore = () => {
    if (examples.length === 0) {
      return;
    }

    setCount(count + 1);
    fetchExamples();
  };

  useEffect(() => {
    fetchExamples();
    // eslint-disable-next-line
  }, [word]);

  const renderSentence = (example: string) => {
    const lowerExample = example.toLowerCase();
    const lowerWord = word.word.toLowerCase();

    const indexes = [];
    let index = lowerExample.indexOf(lowerWord);

    while (index !== -1) {
      indexes.push(index);
      index = lowerExample.indexOf(lowerWord, index + 1);
    }

    const result = [];
    const wordEntries: string[] = [];
    let start = 0;
    for (const i of indexes) {
      result.push(example.slice(start, i));
      wordEntries.push(example.slice(i, i + word.word.length));
      start = i + word.word.length;
    }

    result.push(example.slice(start));
    return result.map((slice, i) => {
      if (i !== result.length - 1) {
        return (
          <>
            {slice}
            <b>{wordEntries[i]}</b>
          </>
        );
      }
      return <>{slice}</>;
    });
  };

  const hdUpgrade = () => {
    navigate(TalaeraRoutes.buildPlanDetailsPath());
    onClose();
  };

  const hdOnSuccessfulUpgrade = () => {
    setShowLimitError(false);
    fetchExamples();
  };

  const generateMoreForPro =
    !!user &&
    user?.student?.has_pro_access &&
    examples.length > 0 &&
    !showLimitError &&
    !isLoading;
  const generateMoreForBasic = !!user && !user?.student?.has_pro_access && !isLoading;

  return (
    <Dialog
      className="examples"
      showDialog={open}
      onClose={onClose}
      closeOnOuterClick={false}
      errors={error}
    >
      <button className="close-icon-button" onClick={onClose}>
        <CloseIcon />
      </button>

      <div className="example-dialog-header">
        <HStack align={"center"} gap={"12px"}>
          <ExampleSentenceIcon />
          <h3>
            <Trans i18nKey={"Tool.exampleOf"}>
              Examples of '{{ word: word.word }}' in use
            </Trans>
          </h3>
        </HStack>
      </div>

      <div className="content">
        {isLoading && <TalaeraSpinner />}

        {!isLoading &&
          examples.length > 0 &&
          examples.map((example, index) => (
            <p key={index} className="sentence">
              <span>{index + 1 + 3 * count}.</span> {renderSentence(example)}
            </p>
          ))}

        {!isLoading && showLimitError && !!user && !user?.student?.has_pro_access && (
          <>
            <p className="no-data-description">{t("Tool.reachLimit")}</p>
            <p className="no-data-description" style={{ marginBottom: 0 }}>
              <Trans
                i18nKey={"Tool.pleaseUpgradeOrTryAgain"}
                values={{ action: t("Common.upgradeToPro") }}
              >
                Please
                <Paywall
                  onSuccess={hdUpgrade}
                  triggerContainerClassName={"upgrade-text"}
                  enable
                  onUpgrade={hdUpgrade}
                >
                  <u className={"upgrade-to-pro"}>{t("Common.upgradeToPro")}</u>
                </Paywall>
                or try again later.
              </Trans>
            </p>
          </>
        )}

        {!isLoading && showLimitError && user?.student?.has_pro_access && (
          <VStack align="center" className="limit-error">
            <ForbiddenImg />
            <h3>{t("Tool.oops")}</h3>
            <p>{t("Tool.haveMoreTomorrow")}</p>
          </VStack>
        )}

        {error && <div style={{ marginTop: 24 }}>{getErrorComponents(error)}</div>}
      </div>

      <div className={"example-dialog-footer-container"}>
        <HStack
          className={"example-dialog-footer"}
          align={"center"}
          justify={"space-between"}
        >
          <div>
            {/*Temporary comment - Will implement feature for this later*/}
            {/*<HStack gap={ "8px" } align={ "center" }>*/}
            {/*  <img src={ translate } height={ 32 } width={ 32 } alt="save word"/>*/}
            {/*  <div className={ "translate-btn" }>Translate Examples</div>*/}
            {/*</HStack>*/}
          </div>

          <HStack align={"center"} gap={"32px"}>
            {(generateMoreForPro || generateMoreForBasic) && (
              <Paywall
                onSuccess={hdOnSuccessfulUpgrade}
                triggerContainerClassName={"upgrade-text"}
                enable={examples.length === 0}
                onUpgrade={hdUpgrade}
              >
                <Button
                  className={`btn more ${examples.length > 0 ? "active" : ""}`}
                  onClick={generateMore}
                >
                  {examples.length === 0 && <LockIcon />} {t("Tool.genMore")}
                </Button>
              </Paywall>
            )}
            <Button className="btn submit" onClick={onClose}>
              {t("Common.Done")}
            </Button>
          </HStack>
        </HStack>
      </div>
    </Dialog>
  );
};

type PracticeVocabDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const PracticeVocabDialog = ({ open, onClose }: PracticeVocabDialogProps) => {
  const fetchPractices = () => {
    // Todo: fetch practices
  };

  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line
  }, []);

  const exampleActions = (
    <div className="form-actions">
      <Button className="btn submit" onClick={onClose}>
        Check Answers
      </Button>
    </div>
  );

  const renderPractice = () => {
    return <></>;
  };

  return (
    <AppDialogWithHeader
      className="examples"
      headerImg={exampleSentenceImg}
      showDialog={open}
      onClose={onClose}
      title="Practice my Vocab"
      formActions={exampleActions}
    >
      <div className="content">{renderPractice()}</div>
    </AppDialogWithHeader>
  );
};
